/**
 * Hook สำหรับรับพิกัด GPS
 *
 * พฤติกรรมตามข้อกำหนด:
 *   - ใช้ watchPosition ระหว่างรอค่าที่ดีขึ้น (ไม่ใช่ getCurrentPosition ครั้งเดียว)
 *   - enableHighAccuracy: true, maximumAge: 0
 *   - เก็บค่าที่ "แม่นที่สุด" ที่เคยได้ ไม่ใช่ค่าล่าสุด
 *   - พอผ่านเกณฑ์แล้วหยุด watch ทันทีเพื่อประหยัดแบตเตอรี่
 *   - หมดเวลาแล้วยังไม่ผ่าน ให้ผู้ใช้เลือกลองใหม่หรือขออนุมัติ
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GpsSettings } from '../../shared/config';
import { meetsThreshold } from '../../shared/gps';

export interface GpsReading {
  latitude: number;
  longitude: number;
  accuracyM: number;
  altitude: number | null;
  heading: number | null;
  speed: number | null;
  positionTimestamp: number;
}

export type GpsPhase =
  | 'idle'
  | 'requesting'
  | 'searching'
  | 'locked'
  | 'timeout'
  | 'denied'
  | 'unavailable'
  | 'unsupported';

export interface GpsState {
  phase: GpsPhase;
  /** ค่าที่แม่นที่สุดที่ได้มาจนถึงตอนนี้ */
  best: GpsReading | null;
  /** จำนวนครั้งที่ได้รับค่าพิกัดใหม่ ใช้แสดงว่าระบบยังทำงานอยู่ */
  samples: number;
  secondsLeft: number;
  errorMessage: string | null;
}

const INITIAL: GpsState = {
  phase: 'idle',
  best: null,
  samples: 0,
  secondsLeft: 0,
  errorMessage: null,
};

function toReading(position: GeolocationPosition): GpsReading {
  const { coords } = position;
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracyM: coords.accuracy,
    altitude: coords.altitude,
    heading: Number.isFinite(coords.heading) ? coords.heading : null,
    speed: Number.isFinite(coords.speed) ? coords.speed : null,
    positionTimestamp: position.timestamp,
  };
}

/** ข้อความภาษาไทยที่บอกทางแก้ ไม่ใช่แค่บอกว่าพัง */
function messageFor(error: GeolocationPositionError): { phase: GpsPhase; message: string } {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        phase: 'denied',
        message:
          'คุณปฏิเสธการเข้าถึงตำแหน่ง กรุณาเปิดสิทธิ์ตำแหน่งให้เว็บนี้ในการตั้งค่าเบราว์เซอร์ แล้วกดลองใหม่',
      };
    case error.POSITION_UNAVAILABLE:
      return {
        phase: 'unavailable',
        message:
          'หาตำแหน่งไม่ได้ ตรวจว่าเปิด GPS ของเครื่องแล้ว และลองออกไปยืนในที่โล่งห่างจากอาคาร',
      };
    case error.TIMEOUT:
      return {
        phase: 'timeout',
        message: 'ใช้เวลาหาสัญญาณนานเกินไป กรุณาลองใหม่ในที่ที่เห็นท้องฟ้าได้กว้างกว่านี้',
      };
    default:
      return { phase: 'unavailable', message: 'เกิดข้อผิดพลาดในการอ่านตำแหน่ง กรุณาลองใหม่' };
  }
}

export function useGeolocation(settings: GpsSettings) {
  const [state, setState] = useState<GpsState>(INITIAL);
  const watchId = useRef<number | null>(null);
  const deadline = useRef<number>(0);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    if (ticker.current !== null) {
      clearInterval(ticker.current);
      ticker.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setState((current) =>
      current.phase === 'searching' ? { ...current, phase: 'idle' } : current,
    );
  }, [cleanup]);

  const start = useCallback(() => {
    cleanup();

    if (!('geolocation' in navigator)) {
      setState({
        ...INITIAL,
        phase: 'unsupported',
        errorMessage:
          'เบราว์เซอร์นี้ไม่รองรับการอ่านตำแหน่ง กรุณาใช้ Chrome หรือ Safari รุ่นล่าสุด',
      });
      return;
    }

    deadline.current = Date.now() + settings.maxWaitSeconds * 1000;
    setState({
      ...INITIAL,
      phase: 'requesting',
      secondsLeft: settings.maxWaitSeconds,
    });

    ticker.current = setInterval(() => {
      const secondsLeft = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000));
      setState((current) => {
        if (current.phase !== 'searching' && current.phase !== 'requesting') return current;
        if (secondsLeft > 0) return { ...current, secondsLeft };

        // หมดเวลา: หยุด watch แต่เก็บค่าที่ดีที่สุดไว้ให้ผู้ใช้ตัดสินใจ
        cleanup();
        return {
          ...current,
          phase: 'timeout',
          secondsLeft: 0,
          errorMessage: current.best
            ? `ครบ ${settings.maxWaitSeconds} วินาทีแล้ว ค่าที่ดีที่สุดคือ ${Math.round(current.best.accuracyM)} เมตร ซึ่งยังไม่ผ่านเกณฑ์ ${settings.thresholdM} เมตร`
            : 'ครบเวลาแล้วแต่ยังไม่ได้รับสัญญาณ กรุณาลองใหม่ในที่โล่ง',
        };
      });
    }, 250);

    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        const reading = toReading(position);
        setState((current) => {
          // เก็บค่าที่แม่นที่สุด ไม่ใช่ค่าล่าสุด เพราะ GPS แกว่งขึ้นลงระหว่างจับสัญญาณ
          const best =
            !current.best || reading.accuracyM < current.best.accuracyM ? reading : current.best;
          const passed = meetsThreshold(best.accuracyM, settings.thresholdM);

          if (passed) {
            // ผ่านเกณฑ์แล้วหยุดทันที ไม่ต้องวัดต่อให้เปลืองแบต
            cleanup();
            return {
              ...current,
              best,
              phase: 'locked',
              samples: current.samples + 1,
              secondsLeft: 0,
              errorMessage: null,
            };
          }
          return {
            ...current,
            best,
            phase: 'searching',
            samples: current.samples + 1,
            errorMessage: null,
          };
        });
      },
      (error) => {
        cleanup();
        const { phase, message } = messageFor(error);
        setState((current) => ({ ...current, phase, errorMessage: message }));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: settings.maxWaitSeconds * 1000,
      },
    );
  }, [cleanup, settings.maxWaitSeconds, settings.thresholdM]);

  const reset = useCallback(() => {
    cleanup();
    setState(INITIAL);
  }, [cleanup]);

  useEffect(() => cleanup, [cleanup]);

  return { ...state, start, stop, reset };
}

/**
 * พิกัดเก่าเกินไปหรือยัง — คำนวณฝั่ง client เพื่อปิดปุ่มส่งไว้ก่อน
 * server ตรวจซ้ำอีกครั้งเสมอและเป็นผู้ตัดสิน
 */
export function useIsPositionFresh(
  reading: GpsReading | null,
  maxAgeSeconds: number,
): { fresh: boolean; ageSeconds: number } {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!reading) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [reading]);

  if (!reading) return { fresh: false, ageSeconds: 0 };
  const ageSeconds = Math.max(0, Math.round((now - reading.positionTimestamp) / 1000));
  return { fresh: ageSeconds <= maxAgeSeconds, ageSeconds };
}
